import React, { useMemo, useState } from 'react';
import InvestmentSectionTabs from './InvestmentSectionTabs';
import { INVESTMENT_FORM_TABS } from './investmentFormUtils';

const baseInputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';

const InputField = ({ label, children, required = false }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500"> *</span>}
        </label>
        {children}
    </div>
);

const NumberInput = ({ value, onChange, placeholder, step = '0.01', min = '0' }) => (
    <input
        type="number"
        step={step}
        min={min}
        value={value}
        onChange={onChange}
        onWheel={(e) => e.target.blur()}
        placeholder={placeholder}
        className={baseInputClass}
    />
);

const InvestmentTabbedForm = ({ value, onChange, showGovernance = true }) => {
    const [activeTab, setActiveTab] = useState(INVESTMENT_FORM_TABS[0].id);

    const updateField = (key, fieldValue) => {
        onChange({ ...value, [key]: fieldValue });
    };

    const tabContent = useMemo(() => {
        switch (activeTab) {
            case 'overview':
                return (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InputField label="Investment Amount" required>
                                <NumberInput
                                    value={value.investmentAmount}
                                    onChange={(e) => updateField('investmentAmount', e.target.value)}
                                    placeholder="1000000"
                                />
                            </InputField>
                            <InputField label="Partly paid amount">
                                <NumberInput
                                    value={value.partlyPaidAmount}
                                    onChange={(e) => updateField('partlyPaidAmount', e.target.value)}
                                    placeholder="50000"
                                />
                            </InputField>
                            <InputField label="Currency" required>
                                <select value={value.currency} onChange={(e) => updateField('currency', e.target.value)} className={baseInputClass}>
                                    <option value="INR">INR (Rs)</option>
                                    <option value="USD">USD ($)</option>
                                    <option value="EUR">EUR (EUR)</option>
                                    <option value="GBP">GBP (GBP)</option>
                                </select>
                            </InputField>
                            <InputField label="Investment Date" required>
                                <input type="date" value={value.investmentDate} onChange={(e) => updateField('investmentDate', e.target.value)} className={baseInputClass} />
                            </InputField>
                            <InputField label="Investment Stage" required>
                                <select value={value.investmentStage} onChange={(e) => updateField('investmentStage', e.target.value)} className={baseInputClass}>
                                    <option value="PRE_SEED">Pre-Seed</option>
                                    <option value="SEED">Seed</option>
                                    <option value="SERIES_A">Series A</option>
                                    <option value="SERIES_B">Series B</option>
                                    <option value="SERIES_C">Series C</option>
                                    <option value="GROWTH">Growth</option>
                                    <option value="OTHER">Other</option>
                                </select>
                            </InputField>
                            <InputField label="Investment Type" required>
                                <select value={value.investmentType} onChange={(e) => updateField('investmentType', e.target.value)} className={baseInputClass}>
                                    <option value="EQUITY">Equity</option>
                                    <option value="CCPS">CCPS</option>
                                    <option value="CCD">CCD</option>
                                    <option value="CONVERTIBLE_NOTE">Convertible Note</option>
                                    <option value="SAFE">SAFE</option>
                                </select>
                            </InputField>
                            <InputField label="Equity Percentage (%)">
                                <NumberInput
                                    value={value.equityPercentage}
                                    onChange={(e) => updateField('equityPercentage', e.target.value)}
                                    placeholder="10.0"
                                />
                            </InputField>
                            {showGovernance && (
                                <InputField label="Access Level">
                                    <select value={value.accessLevel} onChange={(e) => updateField('accessLevel', e.target.value)} className={baseInputClass}>
                                        <option value="FULL">Full (Download)</option>
                                        <option value="LIMITED">Limited (Download)</option>
                                        <option value="VIEW_ONLY">View Only</option>
                                    </select>
                                </InputField>
                            )}
                        </div>

                        {showGovernance && (
                            <div className="flex flex-wrap items-center gap-4">
                                <label className="flex items-center text-sm text-gray-700">
                                    <input type="checkbox" checked={value.leadInvestor} onChange={(e) => updateField('leadInvestor', e.target.checked)} className="mr-2" />
                                    Lead investor
                                </label>
                                <label className="flex items-center text-sm text-gray-700">
                                    <input type="checkbox" checked={value.boardSeat} onChange={(e) => updateField('boardSeat', e.target.checked)} className="mr-2" />
                                    Board seat
                                </label>
                                <label className="flex items-center text-sm text-gray-700">
                                    <input type="checkbox" checked={value.autoGrantAccess} onChange={(e) => updateField('autoGrantAccess', e.target.checked)} className="mr-2" />
                                    Auto-grant access
                                </label>
                            </div>
                        )}
                    </div>
                );
            case 'share-details':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <InputField label="Entry Share Price">
                            <NumberInput value={value.entrySharePrice} onChange={(e) => updateField('entrySharePrice', e.target.value)} placeholder="100.00" />
                        </InputField>
                        <InputField label="Entry Share Equity">
                            <NumberInput value={value.entryShareEquity} onChange={(e) => updateField('entryShareEquity', e.target.value)} placeholder="10.0" />
                        </InputField>
                        <InputField label="No. of CCPS">
                            <NumberInput value={value.numberOfShares} onChange={(e) => updateField('numberOfShares', e.target.value)} step="1" placeholder="1000" />
                        </InputField>
                        <InputField label="Nett Subscription Amount">
                            <NumberInput value={value.nettSubscriptionAmount} onChange={(e) => updateField('nettSubscriptionAmount', e.target.value)} placeholder="45000" />
                        </InputField>
                    </div>
                );
            case 'tranche-1':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <InputField label="Tranche-1 Subscription Amount">
                            <NumberInput value={value.tranche1SubscriptionAmount} onChange={(e) => updateField('tranche1SubscriptionAmount', e.target.value)} placeholder="30000" />
                        </InputField>
                        <InputField label="Tranche-1 No. of CCPS">
                            <NumberInput value={value.tranche1NoCcps} onChange={(e) => updateField('tranche1NoCcps', e.target.value)} step="1" placeholder="300" />
                        </InputField>
                        <InputField label="Tranche-1 Holding Percentage">
                            <NumberInput value={value.tranche1HoldingPercentage} onChange={(e) => updateField('tranche1HoldingPercentage', e.target.value)} placeholder="2.5" />
                        </InputField>
                    </div>
                );
            case 'tranche-2':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <InputField label="Tranche-2 Subscription Amount">
                            <NumberInput value={value.tranche2SubscriptionAmount} onChange={(e) => updateField('tranche2SubscriptionAmount', e.target.value)} placeholder="30000" />
                        </InputField>
                        <InputField label="Tranche-2 No. of CCPS">
                            <NumberInput value={value.tranche2NoCcps} onChange={(e) => updateField('tranche2NoCcps', e.target.value)} step="1" placeholder="300" />
                        </InputField>
                        <InputField label="Tranche-2 Holding Percentage">
                            <NumberInput value={value.tranche2HoldingPercentage} onChange={(e) => updateField('tranche2HoldingPercentage', e.target.value)} placeholder="2.5" />
                        </InputField>
                    </div>
                );
            case 'tranche-3':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <InputField label="Tranche-3 Subscription Amount">
                            <NumberInput value={value.postCompletionSubscriptionAmount} onChange={(e) => updateField('postCompletionSubscriptionAmount', e.target.value)} placeholder="40000" />
                        </InputField>
                        <InputField label="Tranche-3 No. of CCPS">
                            <NumberInput value={value.postCompletionNoCcps} onChange={(e) => updateField('postCompletionNoCcps', e.target.value)} step="1" placeholder="400" />
                        </InputField>
                        <InputField label="Tranche-3 Holding Percentage">
                            <NumberInput value={value.postCompletionHoldingPercentage} onChange={(e) => updateField('postCompletionHoldingPercentage', e.target.value)} placeholder="3.0" />
                        </InputField>
                    </div>
                );
            case 'post-exit':
                return (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <InputField label="Exit Date">
                                <input type="date" value={value.exitDate} onChange={(e) => updateField('exitDate', e.target.value)} className={baseInputClass} />
                            </InputField>
                            <InputField label="Exit Amount">
                                <NumberInput value={value.exitAmount} onChange={(e) => updateField('exitAmount', e.target.value)} placeholder="750000" />
                            </InputField>
                            <InputField label="Exit Valuation">
                                <NumberInput value={value.exitValuation} onChange={(e) => updateField('exitValuation', e.target.value)} placeholder="5000000" />
                            </InputField>
                        </div>
                        <InputField label="Notes">
                            <textarea
                                value={value.notes}
                                onChange={(e) => updateField('notes', e.target.value)}
                                rows="4"
                                className={baseInputClass}
                                placeholder="Additional notes about this investment..."
                            />
                        </InputField>
                    </div>
                );
            default:
                return null;
        }
    }, [activeTab, value]);

    return (
        <div className="space-y-4">
            <InvestmentSectionTabs tabs={INVESTMENT_FORM_TABS} activeTab={activeTab} onChange={setActiveTab} />
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                {tabContent}
            </div>
        </div>
    );
};

export default InvestmentTabbedForm;
